/**
 * Parties of a draft contract: the client, Client 2 (couple package only) and the guardian
 * (minor client only). Saved with a partial update while the contract has no number.
 */

import {
  type ContractDetail,
  type Parties,
  type Person,
  useContractsItemsPartialUpdate,
} from "@agencyos/api-client";
import { Button, SelectField, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { asTexts, type Texts } from "../../components/TranslatedFields";
import { PERSON_FIELDS, type PersonField, errorText, useRefreshContracts } from "./shared";

type Role = keyof Parties;

function PersonBlock({
  who,
  person,
  withRelation,
  missing,
  onChange,
}: {
  who: Role;
  person: Person;
  withRelation: boolean;
  missing: readonly string[];
  onChange: (person: Person) => void;
}) {
  const { t } = useTranslation();
  const fields: PersonField[] = [...PERSON_FIELDS, ...(withRelation ? (["relation"] as const) : [])];
  return (
    <div className="form-section">
      <div className="form-section__head">
        <h3>{t(`contracts.roles.${who}`)}</h3>
        <p className="muted">{t(`contracts.parties.help.${who}`)}</p>
      </div>
      <div className="form-grid">
        {fields.map((field) => {
          const value = person[field] ?? "";
          const set = (next: string) => onChange({ ...person, [field]: next });
          const error = missing.includes(field) && !value.trim() ? t("contracts.parties.required") : undefined;
          if (field === "address")
            return (
              <TextAreaField
                key={field}
                label={t(`contracts.person.${field}`)}
                rows={2}
                value={value}
                error={error}
                onChange={(e) => set(e.target.value)}
              />
            );
          return (
            <TextField
              key={field}
              label={field === "email" ? `${t("contracts.person.email")} (${t("common.optional")})` : t(`contracts.person.${field}`)}
              hint={field === "phone" ? t("contracts.parties.phoneHint") : field === "birth_date" ? t("contracts.parties.dateHint") : undefined}
              type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
              inputMode={field === "phone" ? "tel" : undefined}
              value={value}
              error={error}
              onChange={(e) => set(e.target.value)}
            />
          );
        })}
      </div>
    </div>
  );
}

export function PartiesForm({
  contract,
  editable,
  onDirtyChange,
}: {
  contract: ContractDetail;
  editable: boolean;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const save = useContractsItemsPartialUpdate({ mutation: { onSuccess: refresh } });
  const [parties, setParties] = useState<Parties>(contract.parties ?? {});
  const [minor, setMinor] = useState(Boolean(contract.is_minor));
  const [language, setLanguage] = useState(contract.language ?? "fr");
  const [amendment, setAmendment] = useState<Texts>(asTexts(contract.amendment_text));
  const [dirty, setDirty] = useState(false);
  const isAmendment = contract.kind === "amendment";

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const missing = (role: Role): readonly string[] =>
    contract.blockers.find((b) => b.code === "missing_party" && b.role === role)?.fields ?? [];
  const change = (role: Role) => (person: Person) => {
    setParties((p) => ({ ...p, [role]: person }));
    setDirty(true);
  };

  const onSave = () =>
    save.mutate(
      {
        id: contract.id,
        data: { parties, is_minor: minor, language, ...(isAmendment ? { amendment_text: amendment } : {}) },
      },
      { onSuccess: () => setDirty(false) },
    );

  return (
    <fieldset className="plain-fieldset stack" disabled={!editable}>
      <div className="form-grid">
        <SelectField
          label={t("contracts.fields.language")}
          hint={t("contracts.fields.languageHint")}
          value={language}
          options={(["fr", "en"] as const).map((l) => ({ value: l, label: t(`contracts.languages.${l}`) }))}
          onChange={(e) => {
            setLanguage(e.target.value);
            setDirty(true);
          }}
        />
        <div className="contract-toggle">
          <SoftToggle
            label={t("contracts.fields.isMinor")}
            checked={minor}
            disabled={!editable || isAmendment}
            onChange={(value) => {
              setMinor(value);
              setDirty(true);
            }}
          />
        </div>
      </div>
      {isAmendment ? (
        <div className="form-section">
          <div className="form-section__head">
            <h3>{t("contracts.amendment.text")}</h3>
            <p className="muted">{t("contracts.amendment.textHelp")}</p>
          </div>
          <div className="translated-fields">
            {(["fr", "en"] as const).map((l) => (
              <TextAreaField
                key={l}
                label={t(`contracts.amendment.text_${l}`)}
                rows={6}
                lang={l}
                value={amendment[l] ?? ""}
                onChange={(e) => {
                  setAmendment((a) => ({ ...a, [l]: e.target.value }));
                  setDirty(true);
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
      <PersonBlock who="client" person={parties.client ?? {}} withRelation={false} missing={missing("client")} onChange={change("client")} />
      {contract.requires_second_client ? (
        <PersonBlock who="client2" person={parties.client2 ?? {}} withRelation missing={missing("client2")} onChange={change("client2")} />
      ) : null}
      {minor ? (
        <PersonBlock who="guardian" person={parties.guardian ?? {}} withRelation missing={missing("guardian")} onChange={change("guardian")} />
      ) : null}
      {editable ? (
        <div className="row sticky-actions">
          <Button disabled={!dirty || save.isPending} onClick={onSave}>
            {save.isPending ? t("common.loading") : t("contracts.parties.save")}
          </Button>
          {dirty ? <span className="muted">{t("contracts.parties.unsaved")}</span> : null}
          {save.isSuccess && !dirty ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
          {save.isError ? (
            <span role="alert" className="ko">
              {errorText(save.error, t("common.error"))}
            </span>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
